// @ts-nocheck
/**
 * Notification Service
 * Centralized notification management service for push, in-app, and email notifications
 */

import { getPushNotificationManager } from '../utils/push-notifications';
import type { PushSubscriptionData } from '../utils/push-notifications';

export interface NotificationSettings {
  pushEnabled: boolean;
  emailEnabled: boolean;
  inAppEnabled: boolean;
  customerUpdates: boolean;
  appointmentReminders: boolean;
  projectChanges: boolean;
  orderUpdates: boolean;
  systemNotifications: boolean;
  marketingNotifications: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart: string;
  quietHoursEnd: string;
  timezone: string;
  digestFrequency: 'IMMEDIATE' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'NEVER';
  maxDailyNotifications: number;
}

export interface NotificationAnalytics {
  totalSent: number;
  delivered: number;
  read: number;
  clicked: number;
  failed: number;
  deliveryRate: number;
  readRate: number;
  clickRate: number;
  failureRate: number;
}

export interface NotificationHistory {
  id: string;
  type: string;
  title: string;
  message: string;
  status: string;
  sentAt: string;
  deliveredAt?: string;
  readAt?: string;
  clickedAt?: string;
  actionUrl?: string;
  createdAt: string;
}

export interface SendNotificationRequest {
  title: string;
  message: string;
  type: string;
  recipients?: string[];
  userId?: string;
  channels?: ('push' | 'email' | 'inApp')[];
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  scheduledFor?: string;
  expiresAt?: string;
  data?: Record<string, any>;
  icon?: string;
  image?: string;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
  requireInteraction?: boolean;
  silent?: boolean;
  customerId?: string;
  projectId?: string;
  appointmentId?: string;
  orderId?: string;
}

export class NotificationService {
  private pushManager = getPushNotificationManager();
  private baseUrl = '/api/notifications';

  constructor() {
    this.setupEventListeners();
  }

  // =======================
  // INITIALIZATION
  // =======================

  private setupEventListeners(): void {
    // Listen for service worker messages
    this.pushManager.setupServiceWorkerMessageListener();

    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }

  private handleOnline(): void {
    console.log('📱 Connection restored - syncing notifications');
    // Trigger background sync if available
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        if ('sync' in registration) {
          return registration.sync.register('notification-sync');
        }
      }).catch(console.error);
    }
  }

  private handleOffline(): void {
    console.log('📱 Connection lost - notifications will queue');
  }

  // =======================
  // PERMISSION MANAGEMENT
  // =======================

  public async requestPermission(): Promise<boolean> {
    try {
      return await this.pushManager.requestPermission();
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }

  public async checkPermission(): Promise<boolean> {
    try {
      return await this.pushManager.checkPermission();
    } catch (error) {
      console.error('Failed to check notification permission:', error);
      return false;
    }
  }

  public isSupported(): boolean {
    return this.pushManager.isSupported();
  }

  // =======================
  // SUBSCRIPTION MANAGEMENT
  // =======================

  public async enablePushNotifications(): Promise<boolean> {
    try {
      const subscription = await this.pushManager.subscribe();
      return !!subscription;
    } catch (error) {
      console.error('Failed to enable push notifications:', error);
      return false;
    }
  }

  public async disablePushNotifications(): Promise<boolean> {
    try {
      return await this.pushManager.unsubscribe();
    } catch (error) {
      console.error('Failed to disable push notifications:', error);
      return false;
    }
  }

  public async isPushEnabled(): Promise<boolean> {
    try {
      return await this.pushManager.isSubscribed();
    } catch (error) {
      console.error('Failed to check push subscription status:', error);
      return false;
    }
  }

  public async getCurrentSubscription(): Promise<PushSubscriptionData | null> {
    try {
      return await this.pushManager.getCurrentSubscription();
    } catch (error) {
      console.error('Failed to get current subscription:', error);
      return null;
    }
  }

  // =======================
  // SETTINGS MANAGEMENT
  // =======================

  public async getSettings(): Promise<NotificationSettings | null> {
    try {
      const response = await fetch(`${this.baseUrl}/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return data.data.settings;
      } else {
        throw new Error(data.error || 'Failed to get notification settings');
      }
    } catch (error) {
      console.error('Failed to get notification settings:', error);
      return null;
    }
  }

  public async updateSettings(settings: Partial<NotificationSettings>): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Failed to update notification settings:', error);
      return false;
    }
  }

  // =======================
  // NOTIFICATION SENDING
  // =======================

  public async sendNotification(request: SendNotificationRequest): Promise<{
    success: boolean;
    sent: number;
    failed: number;
    details?: any[];
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          sent: data.data.sent,
          failed: data.data.failed,
          details: data.data.details
        };
      } else {
        return {
          success: false,
          sent: 0,
          failed: 1,
          error: data.error || 'Failed to send notification'
        };
      }
    } catch (error) {
      console.error('Failed to send notification:', error);
      return {
        success: false,
        sent: 0,
        failed: 1,
        error: error.message
      };
    }
  }

  public async sendTestNotification(title?: string, message?: string): Promise<boolean> {
    try {
      return await this.pushManager.sendTestNotification(title, message);
    } catch (error) {
      console.error('Failed to send test notification:', error);
      return false;
    }
  }

  // =======================
  // ANALYTICS AND HISTORY
  // =======================

  public async getAnalytics(days: number = 30, userId?: string): Promise<NotificationAnalytics | null> {
    try {
      const params = new URLSearchParams();
      params.append('days', days.toString());
      if (userId) params.append('userId', userId);

      const response = await fetch(`${this.baseUrl}/analytics?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return data.data.analytics;
      } else {
        throw new Error(data.error || 'Failed to get notification analytics');
      }
    } catch (error) {
      console.error('Failed to get notification analytics:', error);
      return null;
    }
  }

  public async getHistory(options: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
  } = {}): Promise<{
    notifications: NotificationHistory[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  } | null> {
    try {
      const params = new URLSearchParams();
      if (options.page) params.append('page', options.page.toString());
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.type) params.append('type', options.type);
      if (options.status) params.append('status', options.status);

      const response = await fetch(`${this.baseUrl}/history?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.error || 'Failed to get notification history');
      }
    } catch (error) {
      console.error('Failed to get notification history:', error);
      return null;
    }
  }

  // =======================
  // QUEUE MANAGEMENT (Admin)
  // =======================

  public async getQueueStatus(): Promise<{
    pending: number;
    processing: number;
    failed: number;
    total: number;
  } | null> {
    try {
      const response = await fetch(`${this.baseUrl}/queue/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return data.data.queue;
      } else {
        throw new Error(data.error || 'Failed to get queue status');
      }
    } catch (error) {
      console.error('Failed to get queue status:', error);
      return null;
    }
  }

  public async processQueue(): Promise<number> {
    try {
      const response = await fetch(`${this.baseUrl}/queue/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return data.data.processed;
      } else {
        throw new Error(data.error || 'Failed to process queue');
      }
    } catch (error) {
      console.error('Failed to process queue:', error);
      return 0;
    }
  }

  // =======================
  // NOTIFICATION TEMPLATES
  // =======================

  public getNotificationTemplate(type: string, data: Record<string, any> = {}): {
    title: string;
    message: string;
    icon?: string;
    actions?: Array<{ action: string; title: string; icon?: string }>;
  } {
    const templates = {
      CUSTOMER_UPDATE: {
        title: '👤 Client Updated',
        message: `Client ${data.customerName || 'information'} has been updated`,
        actions: [
          { action: 'view_customer', title: 'View Client', icon: '/icons/user.png' }
        ]
      },
      APPOINTMENT_REMINDER: {
        title: '📅 Consultation Reminder',
        message: `Your consultation "${data.appointmentTitle}" is ${data.timeUntil || 'coming up'}`,
        actions: [
          { action: 'view_appointment', title: 'View Details', icon: '/icons/calendar.png' }
        ]
      },
      APPOINTMENT_CONFIRMATION: {
        title: '✅ Appointment Confirmed',
        message: `Your appointment "${data.appointmentTitle}" has been confirmed`,
        actions: [
          { action: 'view_appointment', title: 'View Details', icon: '/icons/calendar.png' }
        ]
      },
      PROJECT_CREATED: {
        title: '🎨 New Project Created',
        message: `Project "${data.projectName}" has been created`,
        actions: [
          { action: 'view_project', title: 'View Project', icon: '/icons/project.png' }
        ]
      },
      PROJECT_UPDATED: {
        title: '🔄 Project Updated',
        message: `Project "${data.projectName}" has been updated`,
        actions: [
          { action: 'view_project', title: 'View Changes', icon: '/icons/project.png' }
        ]
      },
      ORDER_SHIPPED: {
        title: '📦 Order Shipped',
        message: `Your order #${data.orderNumber} has been shipped`,
        actions: [
          { action: 'view_order', title: 'Track Order', icon: '/icons/package.png' }
        ]
      },
      PAYMENT_RECEIVED: {
        title: '💰 Payment Received',
        message: `Payment of $${data.amount} has been received`,
        actions: [
          { action: 'view_order', title: 'View Receipt', icon: '/icons/money.png' }
        ]
      },
      SYSTEM_NOTIFICATION: {
        title: '🔔 System Notification',
        message: data.message || 'You have a new system notification'
      },
      WELCOME: {
        title: '👋 Welcome!',
        message: 'Welcome to Custom Design Manager. Set up your notifications to stay updated.',
        actions: [
          { action: 'open_settings', title: 'Settings', icon: '/icons/settings.png' }
        ]
      }
    };

    return templates[type] || {
      title: 'Notification',
      message: data.message || 'You have a new notification'
    };
  }

  // =======================
  // UTILITY METHODS
  // =======================

  public async trackEvent(type: 'delivered' | 'clicked' | 'closed', notificationData: any): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/analytics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type,
          notificationId: notificationData.notificationId,
          tag: notificationData.tag,
          data: notificationData
        })
      });
    } catch (error) {
      console.error('Failed to track notification event:', error);
    }
  }

  public async initializeForUser(): Promise<void> {
    try {
      // Check if push notifications are already enabled
      const isEnabled = await this.isPushEnabled();
      
      if (!isEnabled) {
        // Check if user has granted permission
        const hasPermission = await this.checkPermission();
        
        if (hasPermission) {
          // Try to enable push notifications automatically
          await this.enablePushNotifications();
        }
      }

      console.log('📱 Notification service initialized for user');
    } catch (error) {
      console.error('Failed to initialize notification service for user:', error);
    }
  }
}

// Singleton instance
let notificationServiceInstance: NotificationService | null = null;

export const getNotificationService = (): NotificationService => {
  if (!notificationServiceInstance) {
    notificationServiceInstance = new NotificationService();
  }
  return notificationServiceInstance;
};

export default NotificationService;